

/** Anything with an `id`, optional or not, of any type. */
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type MaybeWithId = { id?: unknown };
 
/** Narrows a value to one whose `id` is guaranteed present. */
export function hasId<T extends MaybeWithId>(item: T): item is T & Required<Pick<T, "id">> {
  return item.id !== undefined && item.id !== null;
}
 
/**
 * Returns a new array where the item matching `update.id` is shallow-merged
 * with `update`. All other items are returned by reference.
 *
 * If `update.id` is missing the array is returned untouched, and items with
 * no `id` are never matched — so an update can't hit every id-less row at once.
 */
export function patchById<T extends MaybeWithId>(items: T[], update: Partial<T>): T[] {
  if (!hasId(update)) return items;
 
  return items.map((item) => (hasId(item) && item.id === update.id ? { ...item, ...update } : item));
}
 
/**
 * Mutates the matching item in place via Object.assign.
 * Useful inside Immer drafts or when the array is already local state.
 */
export function patchByIdInPlace<T extends MaybeWithId>(items: T[], update: Partial<T>): void {
  if (!hasId(update)) return;
 
  const item = items.find((i) => hasId(i) && i.id === update.id);
  if (item) Object.assign(item, update);
}
 
/**
 * Same as patchById, but the identifying property is configurable
 * (e.g. "userId", "carId", "tag").
 */
export function patchBy<T, K extends keyof T>(items: T[], key: K, update: Partial<T>): T[] {
  const id = update[key];
  if (id === undefined || id === null) return items;
 
  return items.map((item) => (item[key] === id ? { ...item, ...update } : item));
}
 